import React, { useState, useContext, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProductsPage from './pages/ProductsPage';
import LowInventoryPage from './pages/LowInventoryPage';
import SalesPage from './pages/SalesPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import DashboardLayout from './components/layout/DashboardLayout';
import { AuthContext } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ToastProvider } from './context/ToastContext';

// Protected Route Guard
const ProtectedRoute = ({ children }) => {
  const { authToken, selectedShopCode } = useContext(AuthContext);
  const location = useLocation();

  if (!authToken || !selectedShopCode) {
    return <Navigate to={{ pathname: '/login', search: location.search }} replace />;
  }

  return children;
};

// Root & Fallback Redirect Helper preserving query parameters
const FallbackRedirect = () => {
  const { authToken, selectedShopCode } = useContext(AuthContext);
  const location = useLocation();

  if (authToken && selectedShopCode) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to={{ pathname: '/login', search: location.search }} replace />;
};

function App() {
  const initialAuthToken = localStorage.getItem('authToken') || null;
  const initialSelectedShopCode = localStorage.getItem('selectedShopCode') || '';

  const [authToken, setAuthToken] = useState(initialAuthToken);
  const [selectedShopCode, setSelectedShopCode] = useState(initialSelectedShopCode);

  useEffect(() => {
    if (authToken) {
      localStorage.setItem('authToken', authToken);
    } else {
      localStorage.removeItem('authToken');
    }
  }, [authToken]);

  useEffect(() => {
    if (selectedShopCode) {
      localStorage.setItem('selectedShopCode', selectedShopCode);
    } else {
      localStorage.removeItem('selectedShopCode');
    }
  }, [selectedShopCode]);

  const handleLoginSuccess = (token, shopCode) => {
    setAuthToken(token);
    setSelectedShopCode(shopCode);
  };

  const handleLogout = () => {
    setAuthToken(null);
    setSelectedShopCode('');
  };

  return (
    <ThemeProvider>
      <AuthContext.Provider value={{ authToken, setAuthToken, selectedShopCode, setSelectedShopCode }}>
        <ToastProvider>
          <Router>
            <Routes>
              {/* Login Route */}
              <Route
                path="/login"
                element={
                  !authToken || !selectedShopCode ? (
                    <LoginPage onLoginSuccess={handleLoginSuccess} />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )
                }
              />

              {/* Dashboard Layout Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout onLogout={handleLogout}>
                      <DashboardPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/products"
                element={
                  <ProtectedRoute>
                    <DashboardLayout onLogout={handleLogout}>
                      <ProductsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/low-inventory"
                element={
                  <ProtectedRoute>
                    <DashboardLayout onLogout={handleLogout}>
                      <LowInventoryPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/sales"
                element={
                  <ProtectedRoute>
                    <DashboardLayout onLogout={handleLogout}>
                      <SalesPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/reports"
                element={
                  <ProtectedRoute>
                    <DashboardLayout onLogout={handleLogout}>
                      <ReportsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <DashboardLayout onLogout={handleLogout}>
                      <SettingsPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              <Route
                path="/users"
                element={
                  <ProtectedRoute>
                    <DashboardLayout onLogout={handleLogout}>
                      <UsersPage />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* Fallback Redirections */}
              <Route path="/" element={<FallbackRedirect />} />
              <Route path="*" element={<FallbackRedirect />} />
            </Routes>
          </Router>
        </ToastProvider>
      </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;
